// Shared building blocks for the full-screen import flows (markdown,
// spreadsheet): font-face resolution, the PDF-backed preview page, and
// the sidebar control atoms. Both views stay visually identical by
// construction.

import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  FONT_LIBRARY,
  fetchFontBytes,
  libraryEntry,
  pickVariant,
  type CustomFont,
} from "../lib/fonts.ts";
import { base14Name, faceKey } from "../lib/md-theme.ts";
import type { MdFace, PageInfo, RenderResult } from "../lib/protocol.ts";

// ───────────────────────── font resolution ─────────────────────────

/** CSS approximation of a family for the theme-card mocks. */
export function familyCss(family: string): string {
  if (family === "times") return "'Times New Roman', Times, serif";
  if (family === "courier") return "'Courier New', monospace";
  const lib = libraryEntry(family);
  if (lib) return lib.cssFallback;
  return "Arial, Helvetica, sans-serif";
}

/** Resolve all four variants of every family into worker-ready faces.
 *  Library faces fetch (memoised) bytes; unknown families degrade to
 *  Helvetica so a stale stored theme can't wedge the import. */
export async function resolveFaces(
  families: string[],
  customFonts: CustomFont[]
): Promise<MdFace[]> {
  const out: MdFace[] = [];
  const variants: [boolean, boolean][] = [
    [false, false],
    [true, false],
    [false, true],
    [true, true],
  ];
  for (const family of [...new Set(families)]) {
    const lib = libraryEntry(family);
    const custom = customFonts.find((f) => f.id === family);
    for (const [bold, italic] of variants) {
      const key = faceKey(family, bold, italic);
      const b14 = base14Name(family, bold, italic);
      if (b14) {
        out.push({ key, name: b14 });
        continue;
      }
      if (lib) {
        try {
          const v = pickVariant(lib, bold, italic);
          out.push({
            key,
            name: `${lib.label.replace(/\s+/g, "")}-${v.variant}`,
            bytes: await fetchFontBytes(v.url),
          });
          continue;
        } catch {
          /* fetch failed — fall through to Helvetica */
        }
      }
      if (custom) {
        out.push({
          key,
          name: custom.label.replace(/[^A-Za-z0-9-]/g, "") || "Uploaded",
          bytes: custom.bytes.slice(0),
        });
        continue;
      }
      out.push({ key, name: base14Name("helvetica", bold, italic)! });
    }
  }
  return out;
}

// ───────────────────────── preview page ─────────────────────────

export function PreviewPage({
  pageIndex,
  info,
  scale,
  gen,
  renderPage,
}: {
  pageIndex: number;
  info: PageInfo;
  scale: number;
  gen: number;
  renderPage: (pageIndex: number, scale: number) => Promise<RenderResult>;
}) {
  const holderRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(pageIndex < 2);

  useEffect(() => {
    const el = holderRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setVisible(e.isIntersecting);
      },
      { rootMargin: "400px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let dead = false;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    void renderPage(pageIndex, scale * dpr)
      .then((r) => {
        if (dead) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = r.width;
        canvas.height = r.height;
        canvas
          .getContext("2d")!
          .putImageData(new ImageData(new Uint8ClampedArray(r.pixels), r.width, r.height), 0, 0);
      })
      .catch(() => {
        /* superseded by the next generation — keep the old frame */
      });
    return () => {
      dead = true;
    };
  }, [visible, gen, scale, pageIndex, renderPage]);

  return (
    <div
      ref={holderRef}
      className="relative bg-white shadow-md"
      style={{ width: info.width * scale, height: info.height * scale }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
      <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] text-[color:var(--color-fg-muted)]">
        {pageIndex + 1}
      </span>
    </div>
  );
}

// ───────────────────────── control atoms ─────────────────────────

export const fieldCls =
  "h-7 w-full min-w-0 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 text-xs";
export const labelCls = "w-20 shrink-0 text-[11px] text-[color:var(--color-fg-muted)]";

export function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={labelCls}>{label}</span>
      {children}
    </div>
  );
}

export function Num({
  value,
  onChange,
  min,
  max,
  step = 0.5,
  title,
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  title?: string;
}) {
  return (
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      title={title}
      value={value}
      onChange={(e) => {
        const v = Number(e.target.value);
        if (Number.isFinite(v)) onChange(Math.min(max, Math.max(min, v)));
      }}
      className={`${fieldCls} w-16 flex-none`}
    />
  );
}

export function FontSelect({
  value,
  onChange,
  customFonts,
}: {
  value: string;
  onChange: (v: string) => void;
  customFonts: CustomFont[];
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={`${fieldCls} flex-1`}>
      <optgroup label="Built in">
        <option value="helvetica">Helvetica</option>
        <option value="times">Times</option>
        <option value="courier">Courier</option>
      </optgroup>
      <optgroup label="Library">
        {FONT_LIBRARY.map((f) => (
          <option key={f.id} value={`lib:${f.id}`}>
            {f.label}
          </option>
        ))}
      </optgroup>
      {customFonts.length > 0 && (
        <optgroup label="Uploaded">
          {customFonts.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

export function StyleToggles({
  bold,
  italic,
  onBold,
  onItalic,
}: {
  bold: boolean;
  italic: boolean;
  onBold: (v: boolean) => void;
  onItalic: (v: boolean) => void;
}) {
  const btn = (active: boolean) =>
    `inline-flex h-7 w-7 items-center justify-center rounded-md border text-xs ${
      active
        ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
        : "border-[color:var(--color-border)] hover:bg-[color:var(--color-bg-subtle)]"
    }`;
  return (
    <span className="inline-flex gap-1">
      <button type="button" title="Bold" className={btn(bold)} onClick={() => onBold(!bold)}>
        <strong>B</strong>
      </button>
      <button type="button" title="Italic" className={btn(italic)} onClick={() => onItalic(!italic)}>
        <em>I</em>
      </button>
    </span>
  );
}

export function AlignSelect({
  value,
  onChange,
  justify,
}: {
  value: string;
  onChange: (v: "left" | "center" | "right" | "justify") => void;
  justify?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as never)}
      className={`${fieldCls} flex-1`}
    >
      <option value="left">Left</option>
      <option value="center">Centre</option>
      <option value="right">Right</option>
      {justify && <option value="justify">Justify</option>}
    </select>
  );
}

export function Section({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className="rounded-md border border-[color:var(--color-border)]">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-xs font-medium hover:bg-[color:var(--color-bg-subtle)]"
      >
        <i className={`ri-arrow-${open ? "down" : "right"}-s-line text-[color:var(--color-fg-muted)]`} aria-hidden />
        {title}
      </button>
      {open && <div className="space-y-1.5 border-t border-[color:var(--color-border)] p-2">{children}</div>}
    </div>
  );
}
